/**
 * GlowCard — gradient-border dark card with colored blur halo.
 * Based on the user-provided "card section" design prompt.
 *
 * Usage:
 *   <GlowCard feature="sales" delay={0.1}>...</GlowCard>
 */
import React from 'react';
import { cn } from '@/lib/utils';
import { getFeatureColor } from '@/lib/feature-colors';

export const GlowCard = React.forwardRef(function GlowCard(
    {
        feature = 'neutral',
        gradient,           // optional override
        className,
        innerClassName,
        children,
        delay = 0,
        onClick,
        clickable,
        size = 'md',        // 'sm' | 'md' | 'lg' | 'auto'
        'data-testid': testId,
        ...rest
    },
    ref,
) {
    const fc = getFeatureColor(feature);
    const finalGradient = gradient || fc.gradient;

    const heightCls =
        size === 'sm' ? 'min-h-[150px]' :
        size === 'lg' ? 'min-h-[300px]' :
        size === 'auto' ? '' :
        'min-h-[180px]';

    return (
        <div
            ref={ref}
            data-testid={testId}
            onClick={onClick}
            className={cn(
                'glow-card-wrap glow-fade-up relative w-full group',
                (onClick || clickable) && 'cursor-pointer',
                className,
            )}
            style={{ animationDelay: `${delay}s` }}
            {...rest}
        >
            {/* Colored blur halo behind card */}
            <div
                className="glow-card-halo"
                style={{ background: finalGradient }}
            />
            {/* Foreground card with gradient border */}
            <div
                className={cn(
                    'glow-card-body p-5 md:p-6 transition-transform duration-300 group-hover:-translate-y-0.5',
                    heightCls,
                    innerClassName,
                )}
                style={{
                    background: `linear-gradient(#0E0E10, #0E0E10) padding-box, ${finalGradient} border-box`,
                }}
            >
                {children}
            </div>
        </div>
    );
});

export default GlowCard;
